import 'moment/locale/fr';
import { connect } from 'react-redux';
import { get, isEmpty, isEqual } from 'lodash';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, openMediumModal, refreshGenericModal } from '@actions/modal';

import { buildSchema } from '@commons/GenericForm';
import { Button } from '@commons/utils/styledLibraryComponents';
import { isUserAdmin, userHasRoleInpulse } from '@commons/utils/roles';
import Footer from '@commons/Footer/Footer';
import Text from '@commons/Text';
import WhiteCardForm from '@commons/WhiteCardForm';

import { canUpdateAssociatedSupplier } from '@selectors/user';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

import { storeSupplierProfileMapping } from '@services/storeSupplierProfileMapping';
import { supplierProfile as supplierProfilesService } from '@services/supplierProfile';
import { supplier as supplierService } from '@services/supplier';
import catalogsService from '@services/catalogs';
import clientService from '@services/client';
import partnershipsService from '@services/partnerships';

import HeaderPicture from '@admin/components/HeaderPicture';
import OrderParams from '@admin/components/OrderParams';

import '../../Suppliers.css';
import { ButtonsContainer } from '../../components/SupplierDetail/styledComponents';
import { handleSave, isFormValid } from '../../components/SupplierDetailSave';

import { canEditSupplierFromDetailView } from '@selectors/actions/supplierActions';
import {
  Container,
  FormContainer,
  PictureContainer,
  DropdownItem,
  DropdownIcon,
} from './styledComponents';
import {
  SUPPLIER_EDI_CONFIGURATION_INPUTS,
  SUPPLIER_FORM_INPUTS,
  SUPPLIER_INFORMATIONS_INPUTS,
  SUPPLIER_LOCATION_INPUTS,
} from './utils/formInputsConfiguration';

import modalUtils from './utils/modal';

const DEFAULT_SUPPLIER_PICTURE = '/images/inpulse/supplier-kitchen-photo-placeholder.svg';

const ALLOWED_EDI_PARTNER_STEF_SYNC_ORDER_AVAILABILITY = ['STEF_FTP'];

const SupplierDetail = (props) => {
  const {
    handlePrevPage,
    handleSetOpenModalAddNewItemDropdown,
    handleSupplierChange,
    showErrorMessage,
    showMessage,
    supplier: formattedSupplier,
    supplierId,
    suppliersCategories,
    picture,
    setPicture,
    setSelectedPictureFile,
    // redux props
    user,
    client,
    pageLoaded,
    pageLoading,
    openGenericModal,
    refreshGenericModal,
    authorizedActions,
  } = props;

  // Global states
  const [supplier, setSupplier] = useState({
    name: '',
    category: '',
    code: '',
    adress: '',
    city: '',
    postCode: '',
    country: '',
    active: true,
    picture: '',
    enableMultipleOrderOnSameDate: false,
    displayUnavailableProductOrders: true,
    includeExcelInOrderMail: false,
    isKitchen: false,
    catalogId: null,
    languageId: null,
    hidePricesInPurchaseOrder: false,
    displayAmountInclTaxesInPurchaseOrder: false,
    allowDecimalQuantitiesOrder: false,
    hasEdi: false,
  });
  const [hasSSPMWithEdi, setHasSSPMWithEdi] = useState(false);
  const [isFooterDisplayed, setIsFooterDisplayed] = useState(false);

  // Order params
  const [multipleOrder, setMultipleOrder] = useState(false);
  const [displayUnavailableProductOrders, setDisplayUnavailableProductOrders] = useState(true);
  const [xlsWithOrder, setXlsWithOrder] = useState(false);
  const [hidePrices, setHidePrices] = useState(false);
  const [displayAmountInclTaxesInPurchaseOrder, setDisplayAmountInclTaxesInPurchaseOrder] =
    useState(false);
  const [allowDecimalQuantitiesOrder, setAllowDecimalQuantitiesOrder] = useState(false);
  const [hasEdi, setHasEdi] = useState(false);

  // Form inputs and dropdowns
  const [catalogs, setCatalogs] = useState([]);
  const [languagesDropdownItems, setLanguagesDropdownItems] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState({});
  const [ediPartnersItems, setEdiPartnersItems] = useState([]);

  const [areInputsDisabled, setAreInputsDisabled] = useState(supplier.isKitchen);

  const [isAllowedToConfigureStefOrderAvailability, setIsAllowedToConfigureStefOrderAvailability] =
    useState(false);

  // Form
  const [, setForm] = useState(useForm());

  const allInputs = SUPPLIER_FORM_INPUTS({
    categories: suppliersCategories,
    isKitchen: supplier.isKitchen,
    hasMultipleCurrencies: client.hasMultipleCurrencies,
    areInputsDisabled,
    user,
  });

  const supplierForm = useForm({
    defaultValues: {},
    resolver: yupResolver(buildSchema(allInputs)),
  });

  const formFields = useWatch({
    control: supplierForm.control,
  });

  const checkSSPMWithEdi = async (supplierId) => {
    const storeSupplierProfileMappings =
      await storeSupplierProfileMapping.getStoreSupplierProfileMappingsOfSupplier(supplierId);

    const hasSSPMWithEdi = storeSupplierProfileMappings.some(({ isEdi }) => isEdi);

    setHasSSPMWithEdi(hasSSPMWithEdi);
  };

  const fetchLanguages = async () => {
    try {
      const languages = await clientService.getAllLanguages();

      const formattedLanguages = languages.map((language) => ({
        ...language,
        iconSrc: `/images/flags/icon-flag-${language.code}.svg`,
        renderValue: () => (
          <DropdownItem>
            <DropdownIcon src={`/images/flags/icon-flag-${language.code}.svg`} />
            <Text>{language.name}</Text>
          </DropdownItem>
        ),
      }));

      setLanguagesDropdownItems(formattedLanguages);
    } catch {
      showErrorMessage(i18next.t('USERS.DETAILS.FETCH_CLIENT_LANGUAGES_ERROR'));
    }
  };

  const fetchEdiPartners = async () => {
    try {
      const ediPartners = await supplierService.getEdiPartners();

      setEdiPartnersItems(
        ediPartners.map((ediPartner) => ({
          id: ediPartner,
          name: ediPartner,
        })),
      );
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.GET_EDI_PARTNERS_ERROR'));
      setEdiPartnersItems([]);
    }
  };

  const reloadData = async () => {
    pageLoading();

    try {
      if (client.hasLocalCatalogs) {
        const fetchedCatalogs = await catalogsService.getCatalogsByClientId();

        setCatalogs(fetchedCatalogs);
      }

      await Promise.all([fetchLanguages(), fetchEdiPartners()]);

      const result = await supplierProfilesService.getSupplierProfilesOfSupplier(supplierId);
      await checkSSPMWithEdi(supplierId);

      const fetchedSupplier = result.supplier;

      setSupplier({ ...fetchedSupplier, hasEdi: !!result.supplier.ediPartner });

      setAreInputsDisabled(
        !canEditSupplierFromDetailView(authorizedActions) ||
          !canUpdateAssociatedSupplier(user, fetchedSupplier, client),
      );

      /**
       * If ediPartner is a STEF one and the user has the role inpulse,
       * we need to check whether the integration is enabled to configure the STEF order availability
       * If the integration is not enabled, we don't allow the user to configure the STEF order availability
       */
      if (
        ALLOWED_EDI_PARTNER_STEF_SYNC_ORDER_AVAILABILITY.includes(fetchedSupplier.ediPartner) &&
        userHasRoleInpulse(user)
      ) {
        const isIntegrationEnabled = await partnershipsService.isStefIntegrationEnabled();

        setIsAllowedToConfigureStefOrderAvailability(isIntegrationEnabled);
      }
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.GET_SUPPLIER_PROFILE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  useEffect(() => {
    reloadData();
  }, []);

  useEffect(() => {
    if (!!picture) {
      formFields.picture = picture;
    }

    formFields.enableMultipleOrderOnSameDate = multipleOrder;
    formFields.displayUnavailableProductOrders = displayUnavailableProductOrders;
    formFields.includeExcelInOrderMail = xlsWithOrder;
    formFields.hidePricesInPurchaseOrder = hidePrices;
    formFields.displayAmountInclTaxesInPurchaseOrder = displayAmountInclTaxesInPurchaseOrder;
    formFields.allowDecimalQuantitiesOrder = allowDecimalQuantitiesOrder;
    formFields.hasEdi = hasEdi;

    const currentCatalog = supplierForm.getValues('catalog');

    if (currentCatalog) {
      formFields.catalogId = currentCatalog.id;
    }

    const currentLanguage = supplierForm.getValues('language');

    if (currentLanguage) {
      setSelectedLanguage(currentLanguage);
      formFields.languageId = currentLanguage.id;
    }

    const currentEdiPartner = supplierForm.getValues('ediPartnerId');

    if (currentEdiPartner) {
      formFields.ediPartner = currentEdiPartner.id;
    }

    handleSupplierChange(formFields);

    const isFormDirty = get(supplierForm, 'formState.isDirty', false);

    const dirtyFields = Object.keys(get(supplierForm, 'formState.dirtyFields', {}));

    const currentCategory = supplierForm.getValues('category');
    const supplierOriginalCategory = supplier.category || '';

    const supplierOriginalCatalogId = supplier.catalogId || '';

    const hasAtLeastOneDifferentField = dirtyFields.some((field) => {
      if (field === 'catalog') {
        return !!currentCatalog && currentCatalog.id !== supplierOriginalCatalogId;
      }

      if (field === 'category') {
        return (
          !!currentCategory &&
          currentCategory.name.toLowerCase() !== supplierOriginalCategory.toLowerCase()
        );
      }

      return !isEqual(supplier[field], formFields[field]);
    });

    if (
      (isFormDirty && hasAtLeastOneDifferentField) ||
      picture !== supplier.picture ||
      supplier.enableMultipleOrderOnSameDate !== multipleOrder ||
      supplier.displayUnavailableProductOrders !== displayUnavailableProductOrders ||
      supplier.includeExcelInOrderMail !== xlsWithOrder ||
      supplier.hidePricesInPurchaseOrder !== hidePrices ||
      supplier.displayAmountInclTaxesInPurchaseOrder !== displayAmountInclTaxesInPurchaseOrder ||
      supplier.allowDecimalQuantitiesOrder !== allowDecimalQuantitiesOrder ||
      supplier.hasEdi !== hasEdi ||
      // We need this check because sometimes the dirtyFields do not contain the category
      (!!currentCategory &&
        (currentCategory.name || '').toLowerCase() !== supplierOriginalCategory.toLowerCase())
    ) {
      setIsFooterDisplayed(true);
      return;
    }

    setIsFooterDisplayed(false);
  }, [
    formFields,
    picture,
    multipleOrder,
    displayUnavailableProductOrders,
    xlsWithOrder,
    supplier,
    hidePrices,
    displayAmountInclTaxesInPurchaseOrder,
    allowDecimalQuantitiesOrder,
    hasEdi,
  ]);

  useEffect(() => {
    const currentCategory = supplierForm.getValues('category');

    if (!currentCategory || !formattedSupplier.category) {
      return;
    }

    let supplierCategory = '';

    if (suppliersCategories.length) {
      supplierCategory = suppliersCategories.find(({ name }) => {
        if (!name) {
          return false;
        }

        return name.toLowerCase() === formattedSupplier.category.toLowerCase();
      });
    }

    if (
      (!currentCategory && !!formattedSupplier.category) ||
      currentCategory.name !== formattedSupplier.category
    ) {
      supplierForm.setValue('category', supplierCategory);
    }
  }, [formattedSupplier]);

  useEffect(() => {
    if (isEmpty(supplier)) {
      return;
    }

    (async function loadData() {
      setPicture(supplier.picture);

      // Informations

      if (supplier.isKitchen) {
        supplierForm.setValue('category', i18next.t('GENERAL.CENTRAL'));
      } else {
        const category = !!supplier.category ? supplier.category.toLowerCase() : null;

        const supplierCategory = suppliersCategories.find(
          ({ name }) => name.toLowerCase() === category,
        );

        supplierForm.setValue('category', supplierCategory);
      }

      supplierForm.setValue('name', supplier.name);
      supplierForm.setValue('code', supplier.code);
      supplierForm.setValue('siren', supplier.siren);
      supplierForm.setValue('currency', supplier.currency);

      if (client.hasLocalCatalogs) {
        const initialCatalog = catalogs.find(({ id }) => id === supplier.catalogId);

        supplierForm.setValue('catalog', initialCatalog);
      }

      const initialLanguage = languagesDropdownItems.find(({ id }) => id === supplier.languageId);

      supplierForm.setValue('language', initialLanguage);

      // Location
      supplierForm.setValue('adress', supplier.adress);
      supplierForm.setValue('postCode', supplier.postCode);
      supplierForm.setValue('city', supplier.city);
      supplierForm.setValue('country', supplier.country);

      supplierForm.setValue('ediPartner', { id: supplier.ediPartner, name: supplier.ediPartner });
      supplierForm.setValue('externalId', supplier.externalId);
      supplierForm.setValue('hasCommentsInPurchaseOrder', supplier.hasCommentsInPurchaseOrder);

      // Order params
      setMultipleOrder(supplier.enableMultipleOrderOnSameDate);
      setDisplayUnavailableProductOrders(supplier.displayUnavailableProductOrders);
      setHidePrices(supplier.hidePricesInPurchaseOrder);
      setDisplayAmountInclTaxesInPurchaseOrder(supplier.displayAmountInclTaxesInPurchaseOrder);
      setAllowDecimalQuantitiesOrder(supplier.allowDecimalQuantitiesOrder);
      setHasEdi(!!supplier.ediPartner);

      if (!hasSSPMWithEdi) {
        setXlsWithOrder(supplier.includeExcelInOrderMail);
      }

      setForm(supplierForm);
    })();
  }, [supplier]);

  const handleSTEFCredentialSave = async (credentials) => {
    pageLoading();

    try {
      await partnershipsService.updateStefOrderAvailabilityConfig(credentials);

      showMessage(
        i18next.t(
          'ADMIN.SUPPLIERS.SUPPLIER_DETAIL_STEF_ORDER_AVAILABILITY_CONFIGURATION_SAVE_SUCCESS',
        ),
      );
    } catch (error) {
      showErrorMessage(
        i18next.t(
          'ADMIN.SUPPLIERS.SUPPLIER_DETAIL_STEF_ORDER_AVAILABILITY_CONFIGURATION_SAVE_ERROR',
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  const onPropsChange = (credentials, value) => {
    const params = modalUtils.getPropsEditorModalConfig({
      credentials: { ...credentials, ...value },
      props: value,
      onPropsChange,
      handleSave: handleSTEFCredentialSave,
    });

    refreshGenericModal(params);
  };

  const displayModalStefCredentialsEdition = async () => {
    pageLoading();

    try {
      const credentials =
        (await partnershipsService.getStefOrderAvailabilityConfig()) ||
        modalUtils.DEFAULT_CREDENTIALS;

      const { email, clientId, clientSecret } = credentials;

      const params = modalUtils.getPropsEditorModalConfig({
        credentials,
        props: { email, clientId, clientSecret },
        onPropsChange,
        handleSave: handleSTEFCredentialSave,
      });

      openGenericModal(params);
    } catch (error) {
      showErrorMessage(
        i18next.t(
          'ADMIN.SUPPLIERS.SUPPLIER_DETAIL_STEF_ORDER_AVAILABILITY_CONFIGURATION_FETCH_ERROR',
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  const isAllowedToUpdate =
    canEditSupplierFromDetailView(authorizedActions) &&
    canUpdateAssociatedSupplier(user, supplier, client);

  return (
    <Container>
      <FormContainer>
        <PictureContainer>
          <HeaderPicture
            isDefaultThumbnail={!supplier.picture}
            picture={picture || DEFAULT_SUPPLIER_PICTURE}
            readOnly={areInputsDisabled}
            setSelectedFile={setSelectedPictureFile}
            subTitle={
              supplier.isKitchen && i18next.t('ADMIN.SUPPLIERS.KITCHEN_SUPPLIER_DETAIL_SUBTITLE')
            }
            subTitleIcon={supplier.isKitchen ? '/images/inpulse/central-black-small.svg' : null}
            title={supplier.name}
            onPictureChange={setPicture}
          />
          <OrderParams
            allowDecimalQuantitiesOrder={allowDecimalQuantitiesOrder}
            canConfigureStefOrderAvailability={isAllowedToConfigureStefOrderAvailability}
            canIncludeExcelInOrderMail={!hasSSPMWithEdi || hasEdi}
            displayAmountInclTaxesInPurchaseOrder={displayAmountInclTaxesInPurchaseOrder}
            displayModalStefCredentialsEdition={displayModalStefCredentialsEdition}
            displayUnavailableProductOrders={displayUnavailableProductOrders}
            hasEdi={hasEdi}
            hidePrices={hidePrices}
            isAllowedToUpdate={isAllowedToUpdate}
            multipleOrder={multipleOrder}
            setAllowDecimalQuantitiesOrder={setAllowDecimalQuantitiesOrder}
            setDisplayAmountInclTaxesInPurchaseOrder={setDisplayAmountInclTaxesInPurchaseOrder}
            setDisplayUnavailableProductOrders={setDisplayUnavailableProductOrders}
            setHasEdi={setHasEdi}
            setHidePrices={setHidePrices}
            setMultipleOrder={setMultipleOrder}
            setXlsWithOrder={setXlsWithOrder}
            userHasRoleInpulse={userHasRoleInpulse(user)}
            xlsWithOrder={xlsWithOrder}
          />
        </PictureContainer>
        <WhiteCardForm
          form={supplierForm}
          inputs={SUPPLIER_INFORMATIONS_INPUTS({
            userHasRoleAdmin: isUserAdmin(user),
            userHasRoleInpulse: userHasRoleInpulse(user),
            categories: suppliersCategories,
            handleCategoryCreation: handleSetOpenModalAddNewItemDropdown,
            hasMultipleCurrencies: client.hasMultipleCurrencies,
            hasLocalCatalogs: client.hasLocalCatalogs,
            catalogs,
            isKitchen: supplier.isKitchen,
            areInputsDisabled,
            user,
          })}
          shouldDisplayError={false}
          title={i18next.t('ADMIN.SUPPLIERS.SUPPLIER_DETAIL_GENERAL_INFORMATION')}
        />
        <WhiteCardForm
          form={supplierForm}
          inputs={SUPPLIER_LOCATION_INPUTS({
            areInputsDisabled,
            isKitchen: supplier.isKitchen,
            languagesDropdownItems,
            selectedLanguage,
          })}
          shouldDisplayError={false}
          title={i18next.t('ADMIN.SUPPLIERS.SUPPLIER_DETAIL_LOCATION')}
        />
        {userHasRoleInpulse(user) && hasEdi && (
          <WhiteCardForm
            form={supplierForm}
            inputs={SUPPLIER_EDI_CONFIGURATION_INPUTS({
              areInputsDisabled,
              ediPartnersItems,
            })}
            title={i18next.t('ADMIN.SUPPLIERS.SUPPLIER_DETAIL_EDI_CONFIGURATION')}
          />
        )}
      </FormContainer>
      {!areInputsDisabled && isFooterDisplayed && (
        <Footer>
          <ButtonsContainer>
            <Button
              color={'inpulse-outline'}
              handleClick={handlePrevPage}
              icon={'/images/inpulse/close-black-small.svg'}
              label={i18next.t('GENERAL.CANCEL')}
            />
            <Button
              color={'inpulse-default'}
              handleClick={() =>
                handleSave({
                  handlePrevPage: handlePrevPage,
                  pageLoaded: pageLoaded,
                  pageLoading: pageLoading,
                  showMessage: showMessage,
                  supplier: formattedSupplier,
                  supplierId: supplierId,
                  client: client,
                  picture: picture,
                })
              }
              icon={'/images/inpulse/save-white-small.svg'}
              isDisabled={!isFormValid(formattedSupplier, user)}
              label={i18next.t('GENERAL.SAVE')}
            />
          </ButtonsContainer>
        </Footer>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/admin/suppliers/:id/details',
  ),
});

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openMediumModal(params));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierDetail);
